import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Button, Alert, Spinner, Table, Badge } from 'react-bootstrap';
import { Archive, Trash2, Calculator, AlertTriangle } from 'lucide-react';
import Swal from 'sweetalert2';
import { toast } from 'sonner';
import PageHeader from '../../../layouts/PageHeader';
import * as api from '../../../../services/api/timeReportingApi';

/**
 * RetentionPage — admin-only purge of time_entry_history audit rows.
 *
 * Backend: /time-reporting/retention/{preview|purge} (admin-gated via requirePayrollAdmin).
 *
 * Bokföringslagen 7 kap 2§ requires bookkeeping material to be kept for at
 * least 7 years. GDPR art. 5.1e says don't keep it longer than necessary —
 * hence the tenant-configurable retention window.
 */
const RetentionPage = () => {
  const { companyId } = useParams();
  const [retentionYears, setRetentionYears] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [error, setError] = useState(null);

  const loadPreview = useCallback(async (years) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.previewRetention(companyId, years || undefined);
      setPreview(res.data?.data || res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte ladda förhandsvisning.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const handlePurge = async () => {
    const rowsToDelete = preview?.rowsToDelete ?? 0;
    if (rowsToDelete === 0) {
      toast.info('Inga rader äldre än bevarandefönstret.');
      return;
    }
    const effectiveYears = preview?.retentionYears ?? retentionYears;
    const confirm = await Swal.fire({
      title: `Rensa ${rowsToDelete} audit-rader?`,
      html: (
        `<div style="text-align:left;font-size:0.9em">`
        + `<p>Rader äldre än <strong>${effectiveYears} år</strong> i <code>time_entry_history</code> kommer att tas bort permanent.</p>`
        + `<p><strong>⚠ Oåterkallelig åtgärd.</strong> Bokföringsmaterial som är äldre än Bokföringslagens 7-års-krav kan ändå bevaras på andra ställen (lönespecar, verifikationer).</p>`
        + `</div>`
      ),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Rensa',
      cancelButtonText: 'Avbryt',
      confirmButtonColor: '#dc3545',
    });
    if (!confirm.isConfirmed) return;

    setPurging(true);
    setError(null);
    try {
      const res = await api.purgeRetention(companyId, retentionYears ? { retentionYears: Number(retentionYears) } : {});
      const data = res.data?.data || res.data;
      toast.success(`${data.deletedRows || rowsToDelete} rader raderade.`);
      await loadPreview(retentionYears || undefined);
    } catch (err) {
      setError(err.response?.data?.message || 'Rensning misslyckades.');
    } finally {
      setPurging(false);
    }
  };

  return (
    <>
      <PageHeader
        icon={Archive}
        title="Datarensning"
        subtitle="Rensning av tidrapport-historik enligt GDPR och bokföringslagen."
      />

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <div className="card mb-3">
        <div className="card-body">
          <h4 className="mb-3">
            <Archive size={20} className="me-2" />
            Rensning av tidrapport-historik (GDPR)
          </h4>
          <p className="text-muted" style={{ fontSize: 13 }}>
            <code>time_entry_history</code> innehåller audit-rader för alla ändringar av tidrapporter.
            Bokföringslagen (7 kap 2§) kräver minst <strong>7 år</strong>. GDPR art. 5.1e (dataminimering)
            säger att längre lagring ska undvikas. Tenant-inställning <code>time_entry_history_retention_years</code>
            styr standardfönstret.
          </p>

          <div className="d-flex gap-2 align-items-end">
            <Form.Group style={{ maxWidth: 300 }}>
              <Form.Label className="small text-muted">
                Åsidosätt bevarandetid (år, lämna tomt för företagsinställning)
              </Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={20}
                value={retentionYears}
                onChange={(e) => setRetentionYears(e.target.value)}
                placeholder="t.ex. 7"
              />
            </Form.Group>
            <Button
              variant="outline-primary"
              onClick={() => loadPreview(retentionYears || undefined)}
              disabled={loading}
              className="d-flex align-items-center gap-2"
            >
              {loading ? <Spinner size="sm" animation="border" /> : <Calculator size={16} />}
              Förhandsvisa
            </Button>
            <Button
              variant="danger"
              onClick={handlePurge}
              disabled={purging || !preview || (preview?.rowsToDelete ?? 0) === 0}
              className="d-flex align-items-center gap-2"
            >
              {purging ? <Spinner size="sm" animation="border" /> : <Trash2 size={16} />}
              Rensa
            </Button>
          </div>
        </div>
      </div>

      {preview && (
        <div className="card">
          <div className="card-body">
            <h5>Förhandsvisning</h5>
            <Table size="sm" borderless style={{ maxWidth: 500 }}>
              <tbody>
                <tr>
                  <td className="text-muted">Bevarandetid</td>
                  <td className="text-end"><strong>{preview.retentionYears} år</strong></td>
                </tr>
                <tr>
                  <td className="text-muted">Tröskeldatum</td>
                  <td className="text-end">{preview.cutoffDate || '—'}</td>
                </tr>
                <tr>
                  <td className="text-muted">Rader totalt</td>
                  <td className="text-end">{preview.totalRows ?? '—'}</td>
                </tr>
                <tr>
                  <td className="text-muted">Rader att radera</td>
                  <td className="text-end">
                    <Badge bg={(preview.rowsToDelete ?? 0) > 0 ? 'warning' : 'secondary'}>
                      {preview.rowsToDelete ?? 0}
                    </Badge>
                  </td>
                </tr>
              </tbody>
            </Table>
            {(preview.retentionYears ?? 7) < 7 && (
              <Alert variant="warning" className="d-flex align-items-start gap-2 mt-3">
                <AlertTriangle size={16} />
                <div>
                  Bevarandetid &lt; 7 år. Bokföringslagen 7 kap 2§ kräver minst 7 år för bokförda transaktioner —
                  välj endast ett lägre värde för rader som inte är kopplade till en bokförd lön/faktura.
                </div>
              </Alert>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default RetentionPage;
